/* eslint-disable react-refresh/only-export-components -- dialog exports session helpers and the main dialog */
import { AlertTriangle, Scale, BriefcaseBusiness } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { useContent } from "@/hooks/useContent";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import { useState, useEffect } from "react";
import SourceSelectionDialog from "./SourceSelectionDialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@capacit-com/lara-law-shared-ui";
import Workspace from "@/models/workspace/Workspace";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import { Label } from "@capacit-com/lara-law-shared-ui";
import { useWorkspaceDialogSync } from "../../hooks/useWorkspaceDialogSync";
import { WorkspaceSelectionDialog } from "../../blocks/workspace-selection-dialog";
import { WorkspaceContextCapBanner } from "../../blocks/WorkspaceContextCapBanner";

export const SESSION_STORAGE_KEY = "contextDontShowAgain";

export const shouldShowcontext = (): boolean => {
  if (typeof window === "undefined") return true;
  const dontShow = sessionStorage.getItem(SESSION_STORAGE_KEY);
  return dontShow !== "true";
};

type Props = {
  workspaces: Workspace[];
  isLoadingWorkspaces: boolean;
  open: boolean;
  onContinue?: () => void;
  onDismiss?: () => void;
};

const NoContextWarningDialog = ({
  workspaces,
  isLoadingWorkspaces,
  open,
  onContinue,
  onDismiss,
}: Props) => {
  const { t } = useContent();
  const { workspaceContext } = useNewConversationFiltersStore();
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false);

  const [dontShowAgain, setDontShowAgain] = useState(false);
  const {
    selectedItems,
    handleSelectItem,
    handleDeselectWorkspace,
    handleSelectAllWorkspaces,
    commitSelection,
    resetSelection,
  } = useWorkspaceDialogSync({
    workspaceDialogOpen,
    workspaces,
  });

  // Reset checkbox when dialog closes
  useEffect(() => {
    if (!open) {
      setDontShowAgain(false);
    }
  }, [open]);

  const { filters } = useNewConversationFiltersStore();
  const numberOfSources = Object.values(filters).flat().length;
  const numberOfWorkspaces = Object.values(workspaceContext).length;
  const hasWorkspaceContext = numberOfWorkspaces > 0;
  const hasLegalSources = numberOfSources > 0;

  return (
    <>
      <Dialog open={open} onOpenChange={(open) => !open && onDismiss?.()}>
        <DialogContent size="md">
          <DialogTitle className="flex items-center gap-2 text-foreground ">
            <AlertTriangle className="h-5 w-5 text-warning " />
            {t("chat.context.noContextSelected")}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4">
              <p>{t("chat.context.noContextWarning")}</p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSourceDialogOpen(true)}
                  disabled={hasWorkspaceContext}
                  className="w-full justify-start  text-black dark:text-white"
                >
                  <Scale className="size-4 mr-2" />
                  {t("chat.context.addSource")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWorkspaceDialogOpen(true)}
                  disabled={hasLegalSources}
                  className="w-full justify-start  text-black dark:text-white"
                >
                  <BriefcaseBusiness className="size-4 mr-2" />
                  {t("chat.context.addWorkspace")}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground italic">
                {t("chat.context.conversationStartsAutomatically")}
              </p>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="dont-show-again"
                  checked={dontShowAgain}
                  onCheckedChange={(checked) =>
                    setDontShowAgain(checked === true)
                  }
                />
                <Label
                  htmlFor="dont-show-again"
                  className="text-sm font-normal cursor-pointer text-foreground"
                >
                  {t("chat.context.dontShowAgain")}
                </Label>
              </div>
            </div>
          </DialogDescription>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {onDismiss && (
              <Button
                variant="outline"
                onClick={onDismiss}
                className="w-full sm:w-auto"
              >
                {t("chat.sourceSelection.cancel")}
              </Button>
            )}
            {onContinue && (
              <Button
                onClick={() => {
                  if (dontShowAgain) {
                    sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
                  }
                  onContinue();
                }}
                className="w-full sm:w-auto  text-white"
              >
                {t("chat.context.continueAnyway")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SourceSelectionDialog
        open={sourceDialogOpen}
        onOpenChange={setSourceDialogOpen}
        onContinue={() => {
          setSourceDialogOpen(false);
          if (onContinue) {
            if (dontShowAgain) {
              sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
            }
            onContinue?.();
          }
        }}
      />

      <WorkspaceSelectionDialog
        open={workspaceDialogOpen}
        onOpenChange={(open) => {
          if (!open) resetSelection();
          setWorkspaceDialogOpen(open);
        }}
        title={t("chat.context.selectWorkspaceTitle")}
        workspaces={workspaces}
        isLoading={isLoadingWorkspaces}
        emptyMessage={t("chat.context.noWorkspaceFilesFound")}
        selectedItems={selectedItems.map((s) => s.item)}
        onSelectItem={handleSelectItem}
        onDeselectWorkspace={handleDeselectWorkspace}
        onSelectAllWorkspaces={handleSelectAllWorkspaces}
        onConfirm={() => {
          commitSelection();
          setWorkspaceDialogOpen(false);
          onContinue?.();
          if (dontShowAgain) {
            sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
          }
        }}
        confirmLabel={t("chat.context.confirm")}
        showSearch
        explorerDisabled={hasLegalSources}
        warningBanner={
          <div className="flex shrink-0 flex-col gap-2">
            {hasLegalSources ? (
              <div className="p-3 bg-warning/10 border border-warning/30 rounded-md">
                <p className="text-sm text-warning ">
                  {t("chat.context.alreadyHasSourcesMessage")}
                </p>
              </div>
            ) : null}
            <WorkspaceContextCapBanner selectedItems={selectedItems} />
          </div>
        }
      />
    </>
  );
};

export default NoContextWarningDialog;
